import { execa } from "execa"
import type { HookEvent, HookDefinition, HookContext, HookResult, HookHandler } from "./types"
import { BLOCKING_EVENTS } from "./types"
import { logger } from "@/utils/logger"
import { debug } from "@/observability"
import { loadAuthConfig, resolveToken } from "@/utils/auth-store"

// --- Hook Registry: registration, priority ordering, execution ---

interface RegisteredHook {
  event: HookEvent
  definition: HookDefinition
  handler: HookHandler
}

export class HookRegistry {
  private hooks: Map<HookEvent, RegisteredHook[]> = new Map()

  /**
   * Register a hook for an event.
   */
  register(event: HookEvent, definition: HookDefinition): void {
    if (!definition.enabled) return

    const handler = this.createHandler(definition)
    const entry: RegisteredHook = { event, definition, handler }

    const existing = this.hooks.get(event) || []
    existing.push(entry)
    // Sort by priority (lower number = runs first)
    existing.sort((a, b) => a.definition.priority - b.definition.priority)
    this.hooks.set(event, existing)
  }

  /**
   * Execute all hooks for an event. Returns combined result.
   * For blocking events, stops at the first hook that returns { blocked: true }.
   */
  async execute(event: HookEvent, context: HookContext): Promise<HookResult> {
    const registered = this.hooks.get(event)
    if (!registered?.length) {
      return {}
    }

    const canBlock = BLOCKING_EVENTS.includes(event)
    let combinedModified: Record<string, unknown> = {}

    for (const hook of registered) {
      const start = Date.now()
      try {
        const result = await hook.handler({ ...context, ...combinedModified })
        const duration = Date.now() - start

        if (result.modified) {
          combinedModified = { ...combinedModified, ...result.modified }
        }

        if (canBlock && result.blocked) {
          debug.hook(hook.definition.name, duration, "blocked")
          return {
            blocked: true,
            message: result.message || `Blocked by hook: ${hook.definition.name}`,
            modified: combinedModified,
          }
        }

        debug.hook(hook.definition.name, duration, "ok")
      } catch (error: any) {
        const duration = Date.now() - start
        debug.hook(hook.definition.name, duration, `error: ${error.message}`)
        logger.warn(`Hook "${hook.definition.name}" failed: ${error.message}`)
        // Non-blocking hooks swallow errors; blocking hooks propagate
        if (canBlock) {
          return {
            blocked: true,
            message: `Hook "${hook.definition.name}" errored: ${error.message}`,
          }
        }
      }
    }

    return { modified: Object.keys(combinedModified).length ? combinedModified : undefined }
  }

  /**
   * Register an in-process handler for an event. Used by modules that ship
   * their hook behavior as plain JS functions rather than as command/script/
   * prompt definitions loaded from disk (e.g. the workflow engine's
   * subscribers to on:gitlab-issue / on:gitlab-pipeline / post:response).
   * Priority follows the same rule as register(): lower runs first.
   */
  registerHandler(event: HookEvent, name: string, handler: HookHandler, priority: number = 100): void {
    const definition: HookDefinition = { name, type: "script", priority, enabled: true }
    const entry: RegisteredHook = { event, definition, handler }
    const existing = this.hooks.get(event) || []
    existing.push(entry)
    existing.sort((a, b) => a.definition.priority - b.definition.priority)
    this.hooks.set(event, existing)
  }

  /**
   * Check if any hooks are registered for an event.
   */
  has(event: HookEvent): boolean {
    const registered = this.hooks.get(event)
    return !!registered?.length
  }

  /**
   * Clear all hooks (useful for testing).
   */
  clear(): void {
    this.hooks.clear()
  }

  /** Total number of registered hook entries across all events. Used by the
   *  daemon reload path to surface hook-count deltas in the reload summary. */
  size(): number {
    let n = 0
    for (const list of this.hooks.values()) n += list.length
    return n
  }

  /**
   * Create a HookHandler from a HookDefinition.
   */
  private createHandler(definition: HookDefinition): HookHandler {
    switch (definition.type) {
      case "command":
        return this.createCommandHandler(definition)
      case "script":
        return this.createScriptHandler(definition)
      case "prompt":
        return this.createPromptHandler(definition)
      default:
        throw new Error(`Unknown hook type: ${definition.type}`)
    }
  }

  /**
   * Command hook: runs a shell command with {{variable}} interpolation.
   * Exit code 0 = success, non-zero = blocked (for blocking events).
   */
  private createCommandHandler(definition: HookDefinition): HookHandler {
    return async (context: HookContext): Promise<HookResult> => {
      if (!definition.command) {
        return {}
      }

      // Interpolate {{variable}} placeholders
      let cmd = definition.command
      cmd = cmd.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
        return String(context[key] ?? "")
      })

      const result = await execa("sh", ["-c", cmd], {
        cwd: context.cwd || process.cwd(),
        reject: false,
        timeout: 60_000,
        env: process.env,
      })

      if (result.exitCode !== 0) {
        return {
          blocked: true,
          message: result.stderr || result.stdout || `Command hook "${definition.name}" failed`,
        }
      }

      return {}
    }
  }

  /**
   * Script hook: imports and runs a JS/TS module that exports a handler function.
   */
  private createScriptHandler(definition: HookDefinition): HookHandler {
    return async (context: HookContext): Promise<HookResult> => {
      if (!definition.script) {
        return {}
      }

      const scriptPath = definition.script.startsWith("/")
        ? definition.script
        : `${context.cwd || process.cwd()}/${definition.script}`

      const mod = await import(scriptPath)
      const handler = mod.default || mod.handler || mod

      if (typeof handler === "function") {
        return await handler(context)
      }

      return {}
    }
  }

  /**
   * Prompt hook: single-turn LLM call that can block or modify the operation.
   * The prompt template supports {{variable}} interpolation from hook context.
   * The LLM must respond with JSON: { "action": "allow"|"block", "message": "...", "modified": {...} }
   */
  private createPromptHandler(definition: HookDefinition): HookHandler {
    return async (context: HookContext): Promise<HookResult> => {
      if (!definition.prompt) {
        return {}
      }

      // Check credentials are available (don't prompt interactively)
      const providerName = definition.provider || loadAuthConfig()?.provider || "claude-code"
      const resolved = resolveToken()
      if (!resolved && providerName !== "claude-code") {
        logger.warn(
          `Prompt hook "${definition.name}" skipped: no credentials available for provider "${providerName}"`
        )
        return {}
      }

      try {
        // Lazy import to avoid circular deps
        const { createProvider } = await import("@/agent/providers")
        const provider = createProvider(providerName as any, resolved?.token)

        // Interpolate {{variable}} placeholders in the prompt template
        let prompt = definition.prompt
        prompt = prompt.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
          return String(context[key] ?? "")
        })

        const systemPrompt = `You are a hook validator for agentx. Analyze the following context and respond with ONLY a JSON object (no markdown, no code fences).

Response format:
{"action": "allow", "message": "optional explanation"}
OR
{"action": "block", "message": "reason for blocking"}
OR
{"action": "allow", "message": "optional", "modified": {"key": "new value"}}

The "modified" field is optional and can contain updated values to pass forward.`

        const result = await provider.generate(
          [
            { role: "system", content: systemPrompt },
            { role: "user", content: prompt },
          ],
          {
            model: definition.model,
            maxTokens: 1024,
          }
        )

        // Parse the LLM response as JSON
        const content = result.content.trim()
        // Try to extract JSON from the response (handle markdown fences)
        const jsonMatch = content.match(/\{[\s\S]*\}/)
        if (!jsonMatch) {
          logger.warn(`Prompt hook "${definition.name}": LLM response was not valid JSON, allowing`)
          return {}
        }

        const parsed = JSON.parse(jsonMatch[0])

        if (parsed.action === "block") {
          return {
            blocked: true,
            message: parsed.message || `Blocked by prompt hook: ${definition.name}`,
          }
        }

        if (parsed.modified) {
          return { modified: parsed.modified }
        }

        return {}
      } catch (error: any) {
        // Don't block on hook failures — log and continue
        logger.warn(`Prompt hook "${definition.name}" failed: ${error.message}`)
        return {}
      }
    }
  }
}
